<template>
  <div
    ref="container"
    :class="[
      'sf-virtual-table__container',
      {
        'sf-virtual-table__container--horizontally-scrollable':
          horizontallyScrollable,
      },
    ]"
  >
    <div class="sf-virtual-table__scroll-wrapper" :style="scrollWrapperStyle">
      <div
        class="sf-virtual-table__header tw-bg-sf-snow-light"
        data-testid="sf-virtual-table-header"
        :style="headerStyle"
      >
        <div
          v-if="withSelector"
          class="tw-flex tw-justify-end tw-bg-sf-snow-light"
          :style="selectorStickyStyle"
        >
          <SfCheckbox
            v-model="writableSelect"
            :disabled="disableSelector"
          ></SfCheckbox>
        </div>
        <slot name="header" />
      </div>
      <DynamicScroller
        class="sf-full-width rs-companies-list-table sf-virtual-table__scroller"
        :items="items"
        :min-item-size="itemSize"
        :size-dependencies="sizeDependencies"
        :key-field="keyField"
        @resize="getPaddingOffset"
      >
        <template #default="{ item, active }">
          <div
            class="sf-virtual-table__row"
            :class="{
              'sf-virtual-table__row--active':
                activeRowKey !== null && item[keyField] === activeRowKey,
            }"
          >
            <slot name="default" :item="item" :active="active" />
          </div>
        </template>
      </DynamicScroller>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUpdate,
  onMounted,
  onUnmounted,
  provide,
  readonly,
  ref,
  toRef,
  watch,
} from 'vue'
import { DynamicScroller } from 'vue-virtual-scroller'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox'
import { getScrollbarWidth } from '@app/utils/domHelpers'
import { useSelectorSticky } from '@app/shared/vue/composables/use-selector-sticky'

const props = defineProps({
  keyField: {
    type: String,
    required: true,
  },
  minCellWidth: {
    type: Number,
    default: 0,
  },
  items: {
    type: Array,
    required: true,
  },
  itemSize: {
    type: Number,
    required: true,
  },
  rowSize: {
    type: Number,
    required: true,
  },
  sizeDependencies: {
    type: Array,
    default: () => [],
  },
  withSelector: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  disableSelector: {
    type: Boolean,
    default: false,
  },
  resizable: {
    type: Boolean,
    default: false,
  },
  horizontallyScrollable: {
    type: Boolean,
    default: false,
  },
  stickyColumns: {
    type: Number,
    default: 0,
  },
  // When set, the row whose keyField matches is highlighted (same color as hover)
  activeRowKey: {
    type: [String, Number],
    default: null,
  },
})

const emit = defineEmits(['selectAllRows'])

const container = ref(null)
const paddingOffset = ref(0)
const headerCellCounter = ref(0)
const columnWidths = ref([])
const isResizingColumn = ref(false)
const currentResizingIndex = ref(null)
const resizedColumns = ref(new Set())
const scrollLeft = ref(0)

const minColumnWidth = computed(() => props.minCellWidth || 120)

const addResizedColumn = (index) => {
  const next = new Set(resizedColumns.value)
  next.add(index)
  resizedColumns.value = next
}

const resetResizedColumns = () => {
  resizedColumns.value = new Set()
}

const gridTemplateColumns = computed(() => {
  const selector = props.withSelector ? '25px' : ''

  if (props.resizable && columnWidths.value.length > 0) {
    const columnDefs = columnWidths.value
      .map((width, index) => {
        const isFixed =
          resizedColumns.value.has(index) ||
          (isResizingColumn.value && currentResizingIndex.value === index)
        return isFixed ? `${width}px` : `minmax(${width}px, 1fr)`
      })
      .join(' ')

    return selector ? `${selector} ${columnDefs}` : columnDefs
  }

  return `${selector} repeat(${props.rowSize}, minmax(${props.minCellWidth}px, 1fr))`
})

const minTotalWidth = computed(() => {
  const selectorWidth = props.withSelector ? 25 : 0

  // When resizable, use actual column widths (which may be larger than min)
  if (props.resizable && columnWidths.value.length > 0) {
    const totalColumnWidth = columnWidths.value.reduce(
      (sum, width) => sum + width,
      0
    )
    return selectorWidth + totalColumnWidth
  }

  return selectorWidth + props.rowSize * minColumnWidth.value
})

const scrollWrapperStyle = computed(() => {
  if (!props.horizontallyScrollable) {
    return {}
  }
  return {
    'min-width': `${minTotalWidth.value}px`,
  }
})

const headerStyle = computed(() => ({
  padding: `0 ${paddingOffset.value}px 0 0`,
  'grid-template-columns': gridTemplateColumns.value,
}))

const writableSelect = computed({
  get: () => props.selected,
  set: (newValue) => {
    emit('selectAllRows', newValue)
  },
})

const getPaddingOffset = () => {
  const element = container.value

  if (element) {
    const scroller = element.querySelector('.sf-virtual-table__scroller')
    paddingOffset.value = getScrollbarWidth(scroller)
  }
}

const registerHeaderCell = () => {
  const index = headerCellCounter.value
  headerCellCounter.value += 1
  return index
}

const prepareColumnResize = (columnIndex, initialWidth) => {
  const headerEl = container.value?.querySelector('.sf-virtual-table__header')
  let width = initialWidth

  if ((width === null || width === undefined) && headerEl) {
    const cells = headerEl.querySelectorAll('.sf-virtual-table__header-cell')
    const cell = cells[columnIndex]
    width = cell?.clientWidth
  }

  if (width === null || width === undefined) return
  if (width <= 0) return

  width = Math.max(minColumnWidth.value, width)
  columnWidths.value[columnIndex] = width
  addResizedColumn(columnIndex)
}

const resizeColumn = (columnIndex, newWidth) => {
  const width = Math.max(minColumnWidth.value, newWidth)

  // When horizontally scrollable, allow columns to expand beyond container
  if (props.horizontallyScrollable) {
    columnWidths.value[columnIndex] = width
    addResizedColumn(columnIndex)
    return
  }

  const headerEl = container.value?.querySelector('.sf-virtual-table__header')
  const containerWidth = headerEl?.clientWidth ?? 0
  const selectorWidth = props.withSelector ? 25 : 0

  let totalWidth = selectorWidth
  columnWidths.value.forEach((w, idx) => {
    if (idx === columnIndex) {
      totalWidth += width
    } else {
      const isFixed =
        resizedColumns.value.has(idx) ||
        (isResizingColumn.value && currentResizingIndex.value === idx)
      totalWidth += isFixed ? w : minColumnWidth.value
    }
  })

  const finalWidth =
    totalWidth > containerWidth
      ? Math.max(minColumnWidth.value, width - (totalWidth - containerWidth))
      : width

  columnWidths.value[columnIndex] = finalWidth
  addResizedColumn(columnIndex)
}

const setResizingColumn = (isResizing, columnIndex = null) => {
  isResizingColumn.value = isResizing
  currentResizingIndex.value = isResizing ? columnIndex : null
}

const captureColumnWidths = () => {
  const headerEl = container.value?.querySelector('.sf-virtual-table__header')
  if (!headerEl) return

  const cells = headerEl.querySelectorAll('.sf-virtual-table__header-cell')

  if (currentResizingIndex.value !== null) {
    const cell = cells[currentResizingIndex.value]
    if (cell) {
      columnWidths.value[currentResizingIndex.value] = cell.offsetWidth
      addResizedColumn(currentResizingIndex.value)
    }
  }

  columnWidths.value = columnWidths.value.map((w, idx) =>
    resizedColumns.value.has(idx) ? w : minColumnWidth.value
  )
}

watch(
  () => props.rowSize,
  (newSize) => {
    columnWidths.value = Array(newSize).fill(minColumnWidth.value)
    resetResizedColumns()
    currentResizingIndex.value = null
  },
  { immediate: true }
)

// Sticky columns functionality
const onHorizontalScroll = (event) => {
  // Update synchronously for immediate response - no RAF delay
  scrollLeft.value = Math.round(event.target.scrollLeft)
}

const { selectorStickyStyle } = useSelectorSticky({
  withSelector: toRef(props, 'withSelector'),
  backgroundColor: 'var(--sf-color-snow-light)',
  stickyColumns: toRef(props, 'stickyColumns'),
  scrollLeft,
})

onMounted(() => {
  if (props.horizontallyScrollable && props.stickyColumns > 0) {
    container.value?.addEventListener('scroll', onHorizontalScroll, {
      passive: true,
    })
  }
})

onUnmounted(() => {
  container.value?.removeEventListener('scroll', onHorizontalScroll)
})

onBeforeUpdate(() => {
  headerCellCounter.value = 0
})

provide('resizable', readonly(toRef(props, 'resizable')))
provide(
  'horizontallyScrollable',
  readonly(toRef(props, 'horizontallyScrollable'))
)
provide('stickyColumns', readonly(toRef(props, 'stickyColumns')))

// Readonly computed values for children
provide('gridTemplateColumns', readonly(gridTemplateColumns))
provide('minTotalWidth', readonly(minTotalWidth))
provide('scrollLeft', readonly(scrollLeft))
provide('rowSize', readonly(toRef(props, 'rowSize')))

// Functions that mutate parent state - these remain as functions
provide('registerHeaderCell', registerHeaderCell)
provide('prepareColumnResize', prepareColumnResize)
provide('resizeColumn', resizeColumn)
provide('setResizingColumn', setResizingColumn)
provide('captureColumnWidths', captureColumnWidths)
</script>
<style lang="scss" scoped>
@import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-virtual-table {
  &__container {
    @include table;

    height: 100%;
    display: grid;

    // Contain the sticky header/column cells' z-index within this table's own
    // stacking context. Without this they leak into #main-wrapper's context and
    // out-rank the new layout's chrome (subheader z-index: 10), covering menus
    // opened from it (e.g. the insights actions dropdown).
    isolation: isolate;
    grid-template:
      'header' auto
      'data' minmax(300px, 5fr)
      / 1fr;

    &--horizontally-scrollable {
      overflow-x: auto;
    }
  }

  &__scroll-wrapper {
    display: contents;
  }

  &__container--horizontally-scrollable &__scroll-wrapper {
    display: grid;
    grid-template:
      'header' auto
      'data' minmax(0, 1fr)
      / 1fr;
    grid-row: 1 / -1;
    grid-column: 1 / -1;
    min-height: 0;
  }

  &__header {
    grid-area: header;
    display: grid;
    border-bottom: 1px solid var(--sf-color-snow-default);
  }

  &__header-cell {
    @include table-header-cell;
  }

  &__scroller {
    // The rows paint their own white background. Paint the scroller too, so the
    // empty area below the last row stays white instead of showing the page
    // background through the table.
    @include background-color('white');

    grid-area: data;
    overflow-y: auto;
    min-height: 0;
  }

  &__row {
    @include background-color('white');

    // Tint the row's content box (not the wrapper) so the 2px inter-row gutter
    // stays white and the hover/active tint stops exactly at the cells' border.
    &:hover :deep(.sf-virtual-table-row__container),
    &--active :deep(.sf-virtual-table-row__container) {
      @include background-color('snow', 'light');
    }

    // Sticky cells paint their own opaque background, which would otherwise mask
    // the row tint. Tint them too so the entire row is highlighted on both
    // hover and active.
    &:hover :deep(.sf-virtual-table__data-row-cell--sticky),
    &--active :deep(.sf-virtual-table__data-row-cell--sticky) {
      @include background-color('snow', 'light');
    }
  }
}
</style>
